// src/pages/RecipientsPage.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Users, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { getRecipients, addRecipient, removeRecipient } from '../utils/api';

export default function RecipientsPage() {
  const qc = useQueryClient();
  const [newEmail, setNewEmail] = useState('');

  const { data, isLoading } = useQuery({ queryKey: ['recipients'], queryFn: getRecipients });

  const addMutation = useMutation({
    mutationFn: () => addRecipient(newEmail.trim()),
    onSuccess: () => {
      toast.success('Recipient added');
      setNewEmail('');
      qc.invalidateQueries({ queryKey: ['recipients'] });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Invalid email'),
  });

  const removeMutation = useMutation({
    mutationFn: removeRecipient,
    onSuccess: () => { toast.success('Recipient removed'); qc.invalidateQueries({ queryKey: ['recipients'] }); },
  });

  const recipients = data?.recipients || [];

  const handleAdd = (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    addMutation.mutate();
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Recipients</h1>
        <p className="page-subtitle">Manage who receives the weekly analysis emails. Stored in the Data Store — no code changes needed.</p>
      </div>

      <div className="card">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={14} />
          Email Distribution List
          <span className="badge badge-info" style={{ marginLeft: 'auto' }}>{recipients.length} recipients</span>
        </div>

        <form onSubmit={handleAdd} className="form-row" style={{ marginBottom: 20 }}>
          <input
            className="input flex-1"
            type="email"
            placeholder="name@company.com"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
          />
          <button className="btn btn-primary" type="submit" disabled={addMutation.isPending || !newEmail.trim()}>
            <Plus size={15} /> Add
          </button>
        </form>

        {isLoading ? (
          <div className="empty-state"><span className="spinner" /></div>
        ) : recipients.length === 0 ? (
          <div className="empty-state">
            <Mail size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div>No recipients configured.</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Add email addresses above to enable distribution.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Email Address</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((email, i) => (
                  <tr key={email}>
                    <td className="mono" style={{ color: 'var(--text-muted)', width: 40 }}>{i + 1}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Mail size={14} style={{ color: 'var(--text-muted)' }} />
                        {email}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => removeMutation.mutate(email)}
                        disabled={removeMutation.isPending}
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">How it works</div>
        <p className="text-muted" style={{ lineHeight: 1.7 }}>
          Every time a QuickBooks report is processed, Claude's analysis is emailed to all addresses on this list.
          Recipients are stored in the server Data Store and can be updated here at any time without touching code or environment variables.
          Changes take effect immediately on the next pipeline run.
        </p>
      </div>
    </div>
  );
}
